import { createPlugin } from '@customerio/dryad';
import type { ValidationIssue } from '@customerio/dryad';
import { ConditionType } from './condition-types';
import { conditionToNamedBitmap, decodeMessageBitmapNode } from './named-bitmap';
import type { NamedBitmapFilter, NotFilter, SerializedLegacyMessageFilter } from './wire-types';

// The proto behavioral event leaf: `{ named_bitmap: { type: 3, name:
// <base64> } }`, negation as a `{ not: ... }` wrapper — the same leaf key and
// bitmap type as the message condition. What separates the two is the decoded
// name's event type prefix: `e-` (a tracked event) or `p-` (a page view) is
// behavioral and belongs to this row, while the message metrics (`se-`,
// `oe-`, ...) belong to the message row.
//
// The `p-` claim is a deliberate, audited divergence carried over from the
// Ember dryad builder (its ledger, FPS-3345): the backend never writes a
// *named* page bitmap (pages index only as bare `p`/`p_h`), so `p-<name>` is a
// hand-authored dead reference no UI can produce. The Event row preserves the
// decoded type verbatim on serialize — editing a page condition's name keeps
// it a page condition. Likewise the `_h` human flag rides along in state with
// no controls, because the backend treats `_h` as semantic (human-only
// opens/clicks bitmaps) and dropping it would change matching.

/**
 * `eventType` is a TYPE_MAP key: 'event' for `e-` bitmaps, 'page' for `p-`.
 */
const BEHAVIORAL_EVENT_TYPES = ['event', 'page'] as const;

export type BehavioralEventType = (typeof BEHAVIORAL_EVENT_TYPES)[number];

export function behavioralEventTypeOf(eventType: string): BehavioralEventType | null {
  return BEHAVIORAL_EVENT_TYPES.find((behavioralType) => behavioralType === eventType) ?? null;
}

/**
 * `human` mirrors the codec's `_h` suffix handling: the decode strips it into
 * a flag and the encode re-appends it, so a name that arrived with the suffix
 * round-trips byte-identically. The row never renders it — it only survives
 * edits.
 */
export type BehavioralEventConditionState = {
  eventName: string;
  eventType: BehavioralEventType;
  human: boolean;
  inverse: boolean;
};

/**
 * Decodes the node into state, or null when this plugin shouldn't claim it:
 * wrong bitmap type, an undecodable name (bad base64, unknown type prefix), or
 * a decoded event type that isn't behavioral (message metrics belong to the
 * message plugin; attribute_change, device_change, ... stay on the error-node
 * passthrough — dryad's fallback for nodes no plugin claims: re-serialized
 * verbatim, rendered as a remove-only row).
 */
function decodeBehavioralEventBitmap(json: unknown): BehavioralEventConditionState | null {
  const condition = decodeMessageBitmapNode(json);
  if (condition == null) {
    return null;
  }
  const eventType = behavioralEventTypeOf(condition.event.type);
  if (eventType == null) {
    return null;
  }
  return {
    eventName: condition.event.name,
    eventType,
    human: condition.event.human === true,
    inverse: condition.inverse,
  };
}

function serializeBehavioralEventBitmap(state: BehavioralEventConditionState): NamedBitmapFilter | NotFilter {
  const event: SerializedLegacyMessageFilter['event'] = {
    name: state.eventName,
    type: state.eventType,
    filters: {},
  };
  if (state.human) {
    event.human = true;
  }
  const condition: SerializedLegacyMessageFilter = {
    type: 'message',
    event,
    times: 1,
    within: 0,
    inverse: state.inverse,
  };
  return conditionToNamedBitmap(condition);
}

/**
 * A blank name would encode an empty `e-` bitmap, which no editor writes, so
 * the commit gate blocks it.
 */
export function validateBehavioralEventFilter(state: BehavioralEventConditionState): Required<ValidationIssue>[] {
  if (state.eventName === '') {
    return [{ message: "Event name can't be blank.", field: 'eventName' }];
  }
  return [];
}

export const peopleFilterEventPlugin = createPlugin({
  type: ConditionType.Event,
  label: 'Event',
  parse: decodeBehavioralEventBitmap,
  serialize: serializeBehavioralEventBitmap,
  validate: validateBehavioralEventFilter,
  createDefault: (): BehavioralEventConditionState => ({
    eventName: '',
    eventType: 'event',
    human: false,
    inverse: false,
  }),
});
